import {PageHeader} from '../ui/PageHeader';
import {ResponsiveTable} from '../ui/ResponsiveTable';
import { DateOnlyInput } from '../components/DateOnlyInput';
import { formatDateOnly, formatDateTime } from '../dates';
import './operating-appointments.css';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { api } from '../api';
import { saveRequest } from '../ui/save-request';
import { Loading, ErrorState } from '../ui/State';

type Scope = { kind: 'PropertyManager' | 'EngineManager'; key: string; name?: string };
type Option = { id: string; name: string };
type Appointment = {
  id: string;
  name: string;
  startsOn: string;
  endsOn: string | null;
  intent: string;
  state: string;
  actionability: string;
  version: number;
  bindingId: string;
  decisions?: { decision: string; service: string }[];
};
type Directory = { scopes: Scope[]; authorities: { id: string; scopeId: string; issuer: boolean }[]; writesEnabled: boolean; services: Record<string, string[]> };
type Detail = {
  scopeId: string;
  revision: number;
  ownershipStatus: string;
  asOf: string;
  primaries: Appointment[];
  covers: Appointment[];
  bindings: { id: string; display_name: string; verification_id: string; verification_version: number }[];
  designations: { id: string; account_id: string; display_name: string; issuer: boolean; intent: string; version: number }[];
  administrators?: { accountId: string; name: string; issuer: boolean; endsOn: string | null }[];
  reviews: { id: string; cause: string; disposition: string; version: number; owner_id: string | null; due_on: string | null; action_id: string | null }[];
  history: { entity_id: string; command: string; created_at: string; actor_name: string }[];
  attention: { id: string; reason: string }[];
  continuityCoverage: { source: string; coverage: string }[];
};
const verbs: Record<string, string> = {
  VerifyIdentity: 'Verify a person and account',
  CreateDraftAppointment: 'Draft an appointment',
  AuthoriseAppointment: 'Authorise an appointment',
  ReplacePrimary: 'Replace the primary manager',
  AmendPeriod: 'Change appointment dates',
  EndAppointment: 'End an appointment',
  CancelScheduled: 'Cancel a draft or future appointment',
  AuthoriseCover: 'Arrange cover or delegation',
  AmendCover: 'Change cover',
  EndCover: 'End cover',
  IssueDesignation: 'Authorise an administrator',
  RevokeDesignation: 'Revoke administrator authority',
  RemoveDesignationExpiry: 'Keep administrator until changed',
  RevokeVerification: 'Revoke an identity verification',
  ResolveContinuityReview: 'Resolve a continuity review'
};
const decisionLabels: Record<string, string> = {
  building_relationship: 'Building relationship',
  building_outcome_coordination: 'Cross-engine coordination',
  engine_delivery_coordination: 'Engine delivery',
  engine_technical_review: 'Technical review'
};
function historyLabel(command: string) {
  if (verbs[command]) return verbs[command];
  if (command.includes('appointment_cover')) return 'Cover saved';
  if (command.includes('authority_designations')) return 'Administrator authority saved';
  if (command.includes('identity_verifications')) return 'Identity verification saved';
  if (command.includes('continuity_reviews')) return 'Continuity review saved';
  return 'Appointment saved';
}
export function OperatingAppointments() {
  const [context] = useSearchParams();
  const [directory, setDirectory] = useState<Directory>(),
    [scopeIndex, setScopeIndex] = useState('0'),
    [detail, setDetail] = useState<Detail>(),
    [error, setError] = useState<unknown>(),
    [notice, setNotice] = useState(''),
    [busy, setBusy] = useState(false);
  const [command, setCommand] = useState('VerifyIdentity'),
    [selected, setSelected] = useState(''),
    [binding, setBinding] = useState(''),
    [account, setAccount] = useState(''),
    [person, setPerson] = useState('');
  const [search, setSearch] = useState(''),
    [accounts, setAccounts] = useState<Option[]>([]),
    [people, setPeople] = useState<Option[]>([]),
    [nextCursor, setNextCursor] = useState<string | null>(null);
  const [startsOn, setStartsOn] = useState(''),
    [endsOn, setEndsOn] = useState(''),
    [basis, setBasis] = useState(''),
    [issuer, setIssuer] = useState(false),
    [coverKind, setCoverKind] = useState('Cover');
  const [decision, setDecision] = useState('building_relationship'),
    [services, setServices] = useState('*'),
    [disposition, setDisposition] = useState('Exception retained'),
    [dueOn, setDueOn] = useState(''),
    [actionRef, setActionRef] = useState(''),
    [asOf, setAsOf] = useState('');
  const retry = useRef<{ fingerprint: string; key: string; target: string; successor: string } | undefined>(undefined);
  const scope = directory?.scopes[Number(scopeIndex)];
  async function load() {
    const d = await api<Directory>('/api/operating-appointments');
    if (context.has('kind') || context.has('key')) {
      const i = d.scopes.findIndex((s) => s.kind === context.get('kind') && s.key === context.get('key'));
      if (i < 0) throw Error('Ownership administration is unavailable for this scope.');
      setScopeIndex(String(i));
    }
    setDirectory(d);
    return d;
  }
  useEffect(() => {
    void load().catch(setError);
  }, []);
  useEffect(() => {
    if (!scope) return;
    let active = true;
    setDetail(undefined);
    setSelected('');
    setBinding('');
    setAccounts([]);
    setPeople([]);
    setAccount('');
    setPerson('');
    setError(undefined);
    retry.current = undefined;
    setDecision(scope.kind === 'PropertyManager' ? 'building_relationship' : 'engine_delivery_coordination');
    const q = new URLSearchParams({ kind: scope.kind, key: scope.key, ...(asOf ? { asOf } : {}) });
    void api<Detail>('/api/operating-appointments/scope?' + q)
      .then((r) => {
        if (active) setDetail(r);
      })
      .catch((e) => {
        if (active) setError(e);
      });
    return () => {
      active = false;
    };
  }, [scope?.kind, scope?.key, asOf]);
  async function find(more = false) {
    if (!scope) return;
    setError(undefined);
    setBusy(true);
    try {
      const a = await api<{ accounts: Option[]; nextCursor: string | null }>('/api/operating-appointments/candidates', {
        method: 'POST',
        body: JSON.stringify({ scope: { kind: scope.kind, key: scope.key }, q: search, ...(more && nextCursor ? { cursor: nextCursor } : {}) })
      });
      setAccounts(more ? [...accounts, ...a.accounts] : a.accounts);
      setNextCursor(a.nextCursor);
      if (command === 'VerifyIdentity' && !more && (account || a.accounts.length === 1)) {
        const p = await api<{ people: Option[] }>('/api/operating-appointments/candidates', {
          method: 'POST',
          body: JSON.stringify({ scope: { kind: scope.kind, key: scope.key }, q: search, people: true, accountId: account || a.accounts[0]?.id })
        });
        setPeople(p.people);
      }
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  const needsRecord = !['VerifyIdentity', 'CreateDraftAppointment', 'IssueDesignation'].includes(command);
  const cover = command.includes('Cover');
  const recordOptions =
    command === 'ResolveContinuityReview'
      ? detail?.reviews.map((r) => ({ id: r.id, name: r.cause, version: r.version }))
      : command.includes('Designation')
        ? detail?.designations.map((r) => ({ id: r.id, name: r.display_name + ' · ' + (r.issuer ? 'Administrator with delegation' : 'Administrator') + ' · ' + r.intent, version: r.version }))
        : command === 'RevokeVerification'
          ? detail?.bindings.map((r) => ({ id: r.verification_id, name: r.display_name, version: r.verification_version }))
          : cover && command !== 'AuthoriseCover'
            ? detail?.covers
            : detail?.primaries;
  async function save(event: FormEvent) {
    event.preventDefault();
    if (!scope || !detail) return;
    setError(undefined);
    setNotice('');
    setBusy(true);
    try {
      const current = recordOptions?.find((r) => r.id === selected);
      const payload: Record<string, unknown> = {
        command,
        scope: { kind: scope.kind, key: scope.key },
        expectedScopeRevision: detail.revision,
        expectedVersion: current?.version ?? 0,
        basis
      };
      if (command === 'VerifyIdentity') {
        payload.personId = person;
        payload.accountId = account;
        const old = detail.bindings.find((b) => b.id === binding);
        if (old) payload.expectedVersion = old.verification_version;
      }
      if (command === 'IssueDesignation') {
        const parent = directory!.authorities.find((a) => a.scopeId === detail.scopeId && a.issuer);
        if (!parent) throw Error('An independent issuer must authorise administrators.');
        Object.assign(payload, { accountId: account, parentId: parent.id, issuer, startsOn, endsOn: endsOn || null });
      }
      if (command === 'CreateDraftAppointment') Object.assign(payload, { bindingId: binding, startsOn, endsOn: endsOn || null });
      if (command === 'ReplacePrimary') Object.assign(payload, { bindingId: binding, boundary: startsOn, endsOn: endsOn || null });
      if (command === 'AmendPeriod') Object.assign(payload, { startsOn, endsOn: endsOn || null });
      if (command === 'EndAppointment' || command === 'EndCover') payload.endsOn = endsOn;
      if (command === 'AuthoriseCover' || command === 'AmendCover') Object.assign(payload, { startsOn, endsOn, decisions: [{ decision, services: [services] }] });
      if (command === 'AuthoriseCover') {
        Object.assign(payload, { primaryId: selected, bindingId: binding, kind: coverKind });
        payload.expectedVersion = 0;
      }
      if (command === 'ResolveContinuityReview')
        Object.assign(payload, { disposition, ownerId: account || null, dueOn: dueOn || null, actionId: actionRef || null, actionReceiptId: null });
      const fingerprint = JSON.stringify({ ...payload, selected });
      if (retry.current?.fingerprint !== fingerprint) retry.current = { fingerprint, key: crypto.randomUUID(), target: crypto.randomUUID(), successor: crypto.randomUUID() };
      const r = retry.current;
      payload.id = needsRecord && command !== 'AuthoriseCover' ? selected : r.target;
      payload.idempotencyKey = r.key;
      if (command === 'ReplacePrimary') payload.successorId = r.successor;
      const result = await saveRequest<{ current: Detail }>('/api/operating-appointments/commands', { method: 'POST', body: JSON.stringify(payload) });
      setDetail(result.current);
      retry.current = undefined;
      setNotice('Saved. Responsibility, history and any continuity review have been updated.');
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  if (!directory) return <><PageHeader title="Appointments and cover" context="Manage accountable managers, dated cover and continuity. Technical decisions use their original approval controls."/>{error ? <ErrorState error={error} /> : <Loading />}</>;
  return (
    <div className="operating-appointments">
      <PageHeader title="Appointments and cover" context="Manage accountable managers, dated cover and continuity. Technical decisions use their original approval controls."/>
      {error !== undefined && <ErrorState error={error} />} {notice && <p role="status">{notice}</p>}
      {!directory.scopes.length ? (
        <p>No appointment administration scope is currently assigned to you. Contact an authorised issuer.</p>
      ) : (
        <>
          {context.has('kind') && context.has('key') ? (
            <p>{scope?.name}</p>
          ) : (
            <label>
              Building or engine{' '}
              <select
                value={scopeIndex}
                onChange={(e) => {
                  setScopeIndex(e.target.value);
                  setNotice('');
                }}
              >
                {directory.scopes.map((s, i) => (
                  <option key={s.kind + s.key} value={i}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label>
            View responsibility on <DateOnlyInput value={asOf} onChange={setAsOf} />
          </label>
          {!detail ? (
            <Loading />
          ) : (
            <>
              {scope?.kind === 'EngineManager' && <section aria-labelledby="current-engine-administrators">
                <h2 id="current-engine-administrators">Current engine administrators</h2>
                <ul>{detail.administrators?.map(a=><li key={a.accountId}>{a.name}{a.issuer ? ' · Can manage administrators' : ''} · {a.endsOn ? 'Until '+formatDateOnly(a.endsOn) : 'Until changed'}</li>)}</ul>
                <p>Keep at least two administrators for holiday and sickness cover. Their access is separate from the accountable manager below.</p>
              </section>}
              <h2>Responsible managers</h2>
              <p>
                {detail.ownershipStatus} · evaluated {formatDateOnly(detail.asOf)}
              </p>

<ResponsiveTable>
                <thead>
                  <tr>
                    <th>Primary manager</th>
                    <th>From</th>
                    <th>Until, exclusive</th>
                    <th>State</th>
                    <th>Ability to act</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.primaries.map((p) => (
                    <tr key={p.id}>
                      <td>{p.name}</td>
                      <td>{formatDateOnly(p.startsOn)}</td>
                      <td>{p.endsOn ? formatDateOnly(p.endsOn) : 'Ongoing'}</td>
                      <td>{p.state}</td>
                      <td>{p.actionability === 'NotAssessed' ? 'Source checks required' : p.actionability}</td>
                    </tr>
                  ))}
                </tbody>
              </ResponsiveTable>

              <h2>Acting cover and delegation</h2>
              {!detail.covers.length ? (
                <p>No cover recorded.</p>
              ) : (
                <ul>
                  {detail.covers.map((c) => (
                    <li key={c.id}>
                      {c.name}: {formatDateOnly(c.startsOn)} to {formatDateOnly(c.endsOn)}, exclusive · {c.state} ·{' '}
                      {c.actionability === 'NotAssessed' ? 'Source checks required' : c.actionability} ·{' '}
                      {c.decisions?.map((d) => decisionLabels[d.decision] ?? 'Assigned service').join(', ')}
                    </li>
                  ))}
                </ul>
              )}
              <h2>Continuity</h2>
              {detail.attention.map((a) => (
                <p key={a.id}>{a.reason}</p>
              ))}
              {!detail.reviews.length ? (
                <p>No explicit continuity reviews recorded.</p>
              ) : (
                <ul>
                  {detail.reviews.map((r) => (
                    <li key={r.id}>
                      {r.cause} · {r.disposition} · {r.due_on ? formatDateOnly(r.due_on) : 'Due date not set'} · {r.owner_id ? 'Review owner recorded' : 'Unassigned'}
                    </li>
                  ))}
                </ul>
              )}
              <p>Assigned source work must be reviewed through its original workspace. Appointment changes do not transfer or close tasks.</p>
              <h2>Make a change</h2>
              {!directory.writesEnabled ? (
                <p>Appointment changes are currently disabled. Recorded responsibility and history remain available.</p>
              ) : (
                <form onSubmit={save}>
                  <label>
                    Change{' '}
                    <select
                      aria-label="Change"
                      value={command}
                      onChange={(e) => {
                        setCommand(e.target.value);
                        setSelected('');
                        retry.current = undefined;
                      }}
                    >
                      {Object.entries(verbs).filter(([value])=>value!=='RemoveDesignationExpiry'||scope?.kind==='EngineManager').map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </label>
                  {needsRecord && (
                    <label>
                      {command === 'AuthoriseCover' ? 'Primary appointment' : 'Record to change'}{' '}
                      <select
                        aria-label={command === 'AuthoriseCover' ? 'Primary appointment' : 'Record to change'}
                        required
                        value={selected}
                        onChange={(e) => setSelected(e.target.value)}
                      >
                        <option value="">Choose a record</option>
                        {recordOptions?.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  {['VerifyIdentity', 'IssueDesignation', 'ResolveContinuityReview'].includes(command) && (
                    <fieldset>
                      <legend>Find a person</legend>
                      <label>
                        Search internal names <input minLength={2} value={search} onChange={(e) => setSearch(e.target.value)} />
                      </label>
                      <button type="button" disabled={busy || search.trim().length < 2} onClick={() => void find()}>
                        Search
                      </button>
                      <label>
                        System account{' '}
                        <select aria-label="System account" required={command !== 'ResolveContinuityReview'} value={account} onChange={(e) => setAccount(e.target.value)}>
                          <option value="">{command === 'ResolveContinuityReview' ? 'Unassigned' : 'Choose an account'}</option>
                          {accounts.map((o) => (
                            <option key={o.id} value={o.id}>
                              {o.name}
                            </option>
                          ))}
                        </select>
                      </label>
                      {nextCursor && (
                        <button type="button" onClick={() => void find(true)} disabled={busy}>
                          More accounts
                        </button>
                      )}
                      {command === 'VerifyIdentity' && (
                        <>
                          <label>
                            Existing person{' '}
                            <select aria-label="Existing person" value={person} onChange={(e) => setPerson(e.target.value)}>
                              <option value="">Choose the independently confirmed person</option>
                              {people.map((o) => (
                                <option key={o.id} value={o.id}>
                                  {o.name}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label>
                            Existing person record reference <input value={person} onChange={(e) => setPerson(e.target.value)} required pattern="[0-9a-fA-F-]{36}" />
                          </label>
                          <p>
                            Choose an existing linked identity, or use the exact person reference from the independently reviewed identity evidence. This does not grant access to
                            unrelated contacts. Matching names alone is insufficient.
                          </p>
                          <label>
                            Existing verification to refresh, if applicable{' '}
                            <select value={binding} onChange={(e) => setBinding(e.target.value)}>
                              <option value="">New verification</option>
                              {detail.bindings.map((b) => (
                                <option key={b.id} value={b.id}>
                                  {b.display_name}
                                </option>
                              ))}
                            </select>
                          </label>
                        </>
                      )}
                    </fieldset>
                  )}
                  {['CreateDraftAppointment', 'ReplacePrimary', 'AuthoriseCover'].includes(command) && (
                    <label>
                      {cover ? 'Acting person' : 'Appointee'}{' '}
                      <select aria-label={cover ? 'Acting person' : 'Appointee'} required value={binding} onChange={(e) => setBinding(e.target.value)}>
                        <option value="">Choose a verified identity</option>
                        {detail.bindings.map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.display_name}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  {['CreateDraftAppointment', 'ReplacePrimary', 'AmendPeriod', 'AuthoriseCover', 'AmendCover', 'IssueDesignation'].includes(command) && (
                    <label>
                      {command === 'ReplacePrimary' ? 'Replacement date' : 'Start date'} <DateOnlyInput required value={startsOn} onChange={setStartsOn} />
                    </label>
                  )}
                  {['CreateDraftAppointment', 'ReplacePrimary', 'AmendPeriod', 'EndAppointment', 'AuthoriseCover', 'AmendCover', 'EndCover', 'IssueDesignation'].includes(
                    command
                  ) && (
                    <label>
                      End date, exclusive <DateOnlyInput required={cover || command === 'EndAppointment' || (command === 'IssueDesignation' && scope?.kind!=='EngineManager')} value={endsOn} onChange={setEndsOn} />
                      {command==='IssueDesignation'&&scope?.kind==='EngineManager'&&<span>Leave blank to keep this administrator until explicitly changed.</span>}
                    </label>
                  )}
                  {command === 'AuthoriseCover' && (
                    <label>
                      Acting arrangement{' '}
                      <select value={coverKind} onChange={(e) => setCoverKind(e.target.value)}>
                        <option>Cover</option>
                        <option>Delegation</option>
                      </select>
                    </label>
                  )}
                  {['AuthoriseCover', 'AmendCover'].includes(command) && (
                    <>
                      <label>
                        Decision responsibility{' '}
                        <select value={decision} onChange={(e) => setDecision(e.target.value)}>
                          {(scope?.kind === 'PropertyManager'
                            ? [
                                ['building_relationship', 'Building relationship'],
                                ['building_outcome_coordination', 'Cross-engine coordination']
                              ]
                            : [
                                ['engine_delivery_coordination', 'Engine delivery'],
                                ['engine_technical_review', 'Technical review, subject to original source authority']
                              ]
                          ).map(([v, l]) => (
                            <option key={v} value={v}>
                              {l}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        Applicable service{' '}
                        <select value={services} onChange={(e) => setServices(e.target.value)}>
                          <option value="*">All currently listed services</option>
                          {scope?.kind === 'EngineManager' &&
                            directory.services[scope.key]?.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                        </select>
                      </label>
                    </>
                  )}
                  {command === 'IssueDesignation' && (
                    <label>
                      <input type="checkbox" checked={issuer} onChange={(e) => setIssuer(e.target.checked)} />
                      May authorise other administrators within this same scope and period
                    </label>
                  )}
                  {command === 'ResolveContinuityReview' && (
                    <>
                      <label>
                        Disposition{' '}
                        <select value={disposition} onChange={(e) => setDisposition(e.target.value)}>
                          <option>Exception retained</option>
                          <option>Resolved</option>
                        </select>
                      </label>
                      <label>
                        Existing Action reference, if linking a source follow-up{' '}
                        <input value={actionRef} onChange={(e) => setActionRef(e.target.value)} pattern="[0-9a-fA-F-]{36}" />
                      </label>
                      <label>
                        Review due date <DateOnlyInput value={dueOn} onChange={setDueOn} />
                      </label>
                    </>
                  )}
                  <label>
                    Decision or verification basis <textarea required minLength={3} maxLength={500} value={basis} onChange={(e) => setBasis(e.target.value)} />
                  </label>
                  <button disabled={busy} type="submit">
                    {busy ? 'Saving…' : 'Save change'}
                  </button>
                </form>
              )}
              <h2>History</h2>
              <ul>
                {detail.history.map((h, i) => (
                  <li key={i}>
                    {historyLabel(h.command)} · {formatDateTime(h.created_at)}
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </div>
  );
}
